import { html, SP } from '../../../html.js';
import type { VNode } from '../../../html.js';
import type { TenantDetail } from '../../api.js';
import type { DashboardActions, DashboardState } from '../../store.js';
import { ErrorNote, When, fmtInt } from '../../ui/bits.js';
import { TypedConfirm } from '../../ui/confirm.js';
import { BrandCssPanel } from '../../ui/brandcss.js';

/**
 * Tab six: where this site came from, and how to take it down.
 *
 * The source block is the crawl's own audit trail — what was typed, where it
 * actually resolved, what robots.txt said at the time, how many pages were read
 * and how fast. The server has been sending all of it since the tenant route
 * was written and no screen has ever shown it, which is a shame, because it is
 * the answer to "why does this site have three tools when it has thirty pages".
 *
 * The kill switch is last and on the tab nobody opens by accident. It is on a
 * tab at all — rather than at the bottom of one long scroll — for the same
 * reason: the old screen put it two scrolls under the run panel, which meant
 * everybody who was reading the run panel scrolled past it.
 */
export function SettingsTab(props: {
  t: TenantDetail;
  state: DashboardState;
  actions: DashboardActions;
  now: number;
}): VNode {
  const { t, state, actions, now } = props;
  const confirmKey = `tenant:${t.tenantId}`;
  const source = t.source;

  return html`
    <div class="tabpanel">
      <section class="src" aria-labelledby="src-h">
        <h2 class="sec__h" id="src-h">Source of record</h2>
        <dl class="facts">
          <div class="facts__row">
            <dt class="facts__k">Address given</dt>
            <dd class="facts__v"><code>${source.inputUrl}</code></dd>
          </div>
          <div class="facts__row">
            <dt class="facts__k">Resolved to</dt>
            <dd class="facts__v">
              <a href=${source.canonicalOrigin} target="_blank" rel="noopener noreferrer">
                ${source.canonicalOrigin}
              </a>
            </dd>
          </div>
          ${source.redirectChain?.length
            ? html`<div class="facts__row">
                <dt class="facts__k">Through</dt>
                <dd class="facts__v">
                  ${source.redirectChain.map((hop) => html`<code class="hop">${hop}</code>`)}
                </dd>
              </div>`
            : null}
          <div class="facts__row">
            <dt class="facts__k">robots.txt</dt>
            <dd class="facts__v">
              ${source.robots?.disallowAll
                ? 'disallowed everything at crawl time'
                : `${fmtInt(source.robots?.rules?.length ?? 0)} rule${(source.robots?.rules?.length ?? 0) === 1 ? '' : 's'} applied to our agent`}${SP}·${SP}
              read${SP}${When({ iso: source.robots?.fetchedAt ?? null, now, absent: 'not recorded' })}
            </dd>
          </div>
          <div class="facts__row">
            <dt class="facts__k">Crawl</dt>
            <dd class="facts__v">
              ${fmtInt(source.crawl?.pages ?? 0)} pages at ${source.crawl?.maxRps ?? '—'} req/s${SP}·${SP}
              <code>${source.crawl?.userAgent ?? 'agent not recorded'}</code>
            </dd>
          </div>
          <div class="facts__row">
            <dt class="facts__k">Ownership policy</dt>
            <dd class="facts__v">${t.policy.ownership}</dd>
          </div>
        </dl>
      </section>

      ${BrandCssPanel({ t, state, actions })}

      <section class="danger" aria-labelledby="danger-h">
        <h2 class="danger__h" id="danger-h">Unpublish this site</h2>
        <p class="danger__p">
          Removes <code>${t.tenantId}</code> from the registry. <code>/t/${t.tenantId}</code> and${SP}
          <code>/t/${t.tenantId}/mcp</code> stop answering, every agent already holding either URL
          gets a dead endpoint with no redirect, and any custom connector a person added in
          claude.ai fails on its next call.
        </p>
        ${TypedConfirm({
          id: confirmKey,
          phrase: t.tenantId,
          typed: state.confirms[confirmKey] ?? '',
          prompt: html`This is the kill switch, and it takes effect immediately.`,
          actionLabel: `Unpublish ${t.tenantId}`,
          busy: state.unpublishing,
          onType: (value) => actions.setConfirm(confirmKey, value),
          onConfirm: () => actions.unpublish(),
        })}
        ${ErrorNote({ message: state.dangerError })}
      </section>
    </div>
  `;
}
